import { useEffect, useState } from "react";
import { X, Loader2, ChevronLeft, ChevronRight, Inbox } from "lucide-react";
import { STATUS_COLORS } from "../../lib/re";

export const DateInput = ({ value, onChange, disabled, testId, className = "" }) => {
  const toDisplay = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
  const [txt, setTxt] = useState(toDisplay(value));
  useEffect(() => { setTxt(toDisplay(value)); }, [value]);
  const handle = (e) => {
    const d = e.target.value.replace(/\D/g, "").slice(0, 8);
    const masked = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
    setTxt(masked);
    if (d.length === 8) onChange(`${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`);
    else if (d.length === 0) onChange("");
  };
  return <input inputMode="numeric" placeholder="DD/MM/AAAA" disabled={disabled} className={`input-almar ${className}`} value={txt} onChange={handle}
    onBlur={() => { if (txt && txt.replace(/\D/g, "").length !== 8) setTxt(toDisplay(value)); }} data-testid={testId} />;
};

export const Badge = ({ value, label, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_COLORS[value] || "bg-slate-800 text-slate-300 border-slate-700"}`}>
    {label}
  </span>
);

export const Field = ({ label, children, span = 1, hint }) => (
  <label className={`block ${span === 2 ? "sm:col-span-2" : span === 3 ? "sm:col-span-2 lg:col-span-3" : ""}`}>
    <span className="text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-[10px] text-slate-500">{hint}</span>}
  </label>
);

export const Spinner = ({ text = "A carregar..." }) => (
  <div className="flex items-center justify-center gap-2 py-14 text-slate-400 text-sm" data-testid="loading-state">
    <Loader2 size={18} className="animate-spin text-[#C9A84C]" /> {text}
  </div>
);

export const Empty = ({ text, action }) => (
  <div className="card-almar py-14 flex flex-col items-center text-center gap-3" data-testid="empty-state">
    <Inbox size={34} className="text-[#C9A84C]/60" />
    <p className="text-sm text-slate-400 max-w-sm">{text}</p>
    {action}
  </div>
);

export const Pagination = ({ page, pages, total, onChange }) => (
  pages > 1 ? (
    <div className="flex items-center justify-between mt-5 text-sm text-slate-400" data-testid="pagination">
      <span>{total} registos</span>
      <div className="flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="page-prev" className="p-2 rounded-lg border border-[#1E3A56] disabled:opacity-30 hover:border-[#C9A84C]/50"><ChevronLeft size={16} /></button>
        <span data-testid="page-indicator">{page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="page-next" className="p-2 rounded-lg border border-[#1E3A56] disabled:opacity-30 hover:border-[#C9A84C]/50"><ChevronRight size={16} /></button>
      </div>
    </div>
  ) : <p className="mt-5 text-xs text-slate-500">{total} registos</p>
);

export const Modal = ({ title, onClose, children, wide, testId }) => (
  <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
    <div className={`card-almar w-full ${wide ? "max-w-4xl" : "max-w-lg"} my-6`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E3A56]">
        <h3 className="font-display text-lg text-[#C9A84C]">{title}</h3>
        <button onClick={onClose} data-testid="modal-close-btn" className="text-slate-400 hover:text-red-300"><X size={20} /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export const Btn = ({ children, variant = "gold", className = "", ...rest }) => {
  const styles = {
    gold: "bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold",
    ghost: "border border-[#1E3A56] text-slate-300 hover:border-[#C9A84C]/50 hover:text-[#E0C574]",
    danger: "border border-red-500/30 text-red-300 hover:bg-red-500/10",
  };
  return (
    <button {...rest} className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
};

export const Tabs = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 overflow-x-auto border-b border-[#1E3A56] mb-5" data-testid="tabs">
    {tabs.map((t) => (
      <button key={t.id} onClick={() => onChange(t.id)} data-testid={`tab-${t.id}`}
        className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${active === t.id ? "border-[#C9A84C] text-[#E0C574]" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
        {t.label}{t.count != null && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-[#0D1B2A]">{t.count}</span>}
      </button>
    ))}
  </div>
);

export const Timeline = ({ items, testId = "timeline" }) => (
  <div className="space-y-3" data-testid={testId}>
    {(!items || items.length === 0) && <p className="text-sm text-slate-500">Sem atividade registada.</p>}
    {items?.map((h) => (
      <div key={h.id || h.created_at} className="flex gap-3">
        <span className="mt-1.5 w-2 h-2 rounded-full bg-[#C9A84C] flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-slate-200">{h.action || h.note}{h.details ? <span className="text-slate-400"> · {h.details}</span> : null}</p>
          <p className="text-[11px] text-slate-500">{h.user_name || h.author} · {(h.created_at || "").slice(0, 10).split("-").reverse().join("/")} {(h.created_at || "").slice(11, 16)}{h.type ? ` · ${h.type}` : ""}</p>
        </div>
      </div>
    ))}
  </div>
);
